/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay } from 'rxjs/operators';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { ScoreTimeSeriesService } from 'src/app/shared/services/data-api/score-time-series.service';
import { ScoreEventsService } from 'src/app/shared/services/data-api/score-events.service';
import { DateUtils } from 'src/app/shared/utils/date.utils';

// Fixed rolling period (days), matching the chart's default "week" sentiment line.
export const SENTIMENT_ROLLING_DAYS = 7;

/**
 * Timeframe scoping for the panel's data. `time` is URL-style: either a rolling
 * day count (`'7'`) or an explicit range (`'2026-06-01--2026-06-30'`, e.g. a
 * heatmap month). `period` is the score-averaging window in days.
 */
export interface PanelTimeframe {
  time: string;
  period: number;
}

/** Default: the map panel's fixed 7-day rolling window. */
export const DEFAULT_TIMEFRAME: PanelTimeframe = {
  time: String(SENTIMENT_ROLLING_DAYS),
  period: SENTIMENT_ROLLING_DAYS,
};

export interface KeyEventVM {
  id: string;
  type: string;
  name: string;
  contribution: number;
  mentions: number;
}

// Data layer for the country sector board. Provided at the component level so
// the per-country caches live and die with the board.
@Injectable()
export class CountryBoardDataService {
  private childrenCache = new Map<string, Observable<any[]>>();
  private keyEventsCache = new Map<string, Observable<KeyEventVM[]>>();

  constructor(
    private entityInfo: EntityInfoService,
    private scoreTimeSeries: ScoreTimeSeriesService,
    private scoreEvents: ScoreEventsService,
  ) {}

  // Cached (shareReplay) so hover-prefetch and click share one request. Keyed by
  // entity type + id + timeframe so the map's 7-day window and a heatmap month
  // don't collide.
  getChildren$(
    id: string,
    entityType = 'country',
    tf: PanelTimeframe = DEFAULT_TIMEFRAME,
  ): Observable<any[]> {
    const key = `${entityType}:${id}:${tf.time}`;
    let obs = this.childrenCache.get(key);
    if (!obs) {
      obs = this.fetchChildren(id, entityType, tf).pipe(shareReplay(1));
      this.childrenCache.set(key, obs);
    }
    return obs;
  }

  getKeyEvents$(
    id: string,
    entityType = 'country',
    tf: PanelTimeframe = DEFAULT_TIMEFRAME,
  ): Observable<KeyEventVM[]> {
    const key = `${entityType}:${id}:${tf.time}`;
    let obs = this.keyEventsCache.get(key);
    if (!obs) {
      obs = this.fetchKeyEvents(id, entityType, tf).pipe(shareReplay(1));
      this.keyEventsCache.set(key, obs);
    }
    return obs;
  }

  // Companies of the entity, enriched with their rolling sentiment (score_ts,
  // same source as the chart). Country rows keep the home-country filter so only
  // domiciled companies count; industry/index rows use the related set as-is.
  // Last score 0 = no news → NaN (dropped).
  private fetchChildren(
    id: string,
    entityType: string,
    tf: PanelTimeframe,
  ): Observable<any[]> {
    return this.entityInfo
      .getRelatedEntitiesInfo(entityType, id, 'company')
      .pipe(
        catchError((err) => {
          console.error('[market-board] related metadata failed', id, err);
          return of([] as any[]);
        }),
        mergeMap((entities: any) => {
          const list: any[] = Array.isArray(entities) ? entities : [];
          const companies: any[] =
            entityType === 'country'
              ? list.filter((e) => {
                  const home = e?.description?.Country?.entity?.alpha_id;
                  return typeof home === 'string' && home.toLowerCase() === id;
                })
              : list;
          const keys = companies
            .map((e) => e?.compound_key)
            .filter((k: unknown): k is string => typeof k === 'string' && !!k);
          if (keys.length === 0) return of([] as any[]);
          return this.scoreTimeSeries
            .fetchEntityResults(
              { period: tf.period },
              { entities: keys, time: tf.time },
            )
            .pipe(
              map((results) => {
                const scoreByKey: Record<string, number> = {};
                (results ?? []).forEach((r: any) => {
                  const key = r?.entity_info?.compound_key;
                  const ts = (r?.score_ts ?? []) as { score: number | null }[];
                  const last = ts.length ? ts[ts.length - 1]?.score : null;
                  if (key && typeof last === 'number' && last !== 0) {
                    scoreByKey[key] = last;
                  }
                });
                return companies.map((entity: any) => {
                  const s = scoreByKey[entity.compound_key];
                  return { entity, sentiment: typeof s === 'number' ? s : NaN };
                });
              }),
            );
        }),
      );
  }

  private fetchKeyEvents(
    id: string,
    entityType: string,
    tf: PanelTimeframe,
  ): Observable<KeyEventVM[]> {
    const { dateFrom, dateTo, period } = DateUtils.fromTo(tf.time);
    const base = { dateFrom, dateTo, period };
    return forkJoin({
      importance: this.scoreEvents
        .getTopEvents(id, entityType, {
          ...base,
          ranking: 'relative_importance',
          nEvents: 5,
        })
        .pipe(catchError(() => of({ top_events: [] }))),
      counts: this.scoreEvents
        .getTopEvents(id, entityType, {
          ...base,
          ranking: 'absolute_count',
          nEvents: 100,
        })
        .pipe(catchError(() => of({ top_events: [] }))),
    }).pipe(
      map(({ importance, counts }) => {
        const mentionsById = new Map<string, number>();
        (counts?.top_events ?? []).forEach((e) => {
          if (e.id && typeof e.contrib_amount === 'number') {
            mentionsById.set(e.id, Math.round(e.contrib_amount));
          }
        });
        return (importance?.top_events ?? []).slice(0, 5).map((e) => {
          const pct =
            typeof e.contrib_amount === 'number'
              ? Math.round(e.contrib_amount * 100)
              : 0;
          const negative = typeof e.impact === 'number' ? e.impact < 0 : false;
          return {
            id: e.id ?? '',
            type: e.type ?? 'sub_event',
            name: e.name ?? e.id ?? '',
            // Signed contribution: share of the sentiment impact, negative when
            // the event drags sentiment down.
            contribution: negative ? -pct : pct,
            mentions: e.id ? (mentionsById.get(e.id) ?? 0) : 0,
          };
        });
      }),
    );
  }
}
